/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable prettier/prettier */
import { Key, useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import { EyeOutlined, FilterOutlined, SearchOutlined } from '@ant-design/icons'
import { Button, notification, Spin, Tag } from 'antd'
import dayjs from 'dayjs'

import { DateFromToFilter, DeliveryNote, TIME } from '@components'
import { QUERY_KEYS } from '@api'
import { TicketNotifContext, useFilters, useGeneral } from '@contexts'
import {
  useCarriers,
  useCreateTicket,
  useEquipment,
  useGetTickets,
  useMaterials,
  useSuppliers,
  useTickets,
} from '@hooks'
import { Filters, CommonTicketForm, StatusTicketEnum, Ticket } from '@types'
import { theme } from '@styles'

import CollapseExpand from '../../../components/CollapseExpand'
import DriverBarlight from '../../../components/DriverBarlight'
import ExpandableTable from '../../../components/ExpandableTable'
import { MapsModal } from '../../../components/MapsModal/MapsModal'
import {
  ActionsCellContainer,
  ContentContainer,
  StyledButton,
  StyledRow,
  StyledSelect,
} from '../../../components/StyledComponents'
import { useSocket } from '../../../hooks/useSocket'
import { useHandleTicketModal } from '../../../contexts/HandleTicketModalContext'
import prepareDataToExpandableTable from '../../../utils/prepare-data-to-expandable-table'
import { useApproveTicket } from '../AdminDashboard/hooks'
import UpdateTicketModalAdmin from '../UpdateTicketModalAdmin'

const AdminEVouchers = () => {
  const { t } = useTranslation()
  const [isMapsModalOpen, setIsMapsModalOpen] = useState(false)
  const [locationLatitude, setLocationLatitude] = useState<number | undefined>(undefined)
  const [locationLongitude, setLocationLongitude] = useState<number | undefined>(undefined)
  const [locationStreet, setLocationStreet] = useState<string[] | string | undefined>(undefined)
  const [locationCity, setLocationCity] = useState<string[] | string | undefined>(undefined)
  const [locationState, setLocationState] = useState<string[] | string | undefined>(undefined)
  const [locationNumber, setLocationNumber] = useState<string[] | string | undefined>(undefined)
  const [tag, setTag] = useState<string | undefined>(undefined)
  const { setIsTicketModalOpen, setTicketToEdit } = useHandleTicketModal()

  const { filters, setFilters, resetFilters } = useFilters()
  const { changeTicket } = useTickets()
  const { tickets, ticketsIsLoading } = useGetTickets(filters)
  const queryClient = useQueryClient()

  // Conexión WebSocket y actualizaciones de tickets
  const { connect, socket } = useSocket()

  // Conectar a WebSocket cuando el componente se monta
  useEffect(() => {
    connect()
  }, [connect])

  // Escuchar actualizaciones de tickets directamente en el socket
  useEffect(() => {
    if (!socket) return

    const handleTicketUpdate = () => {
      queryClient.invalidateQueries([QUERY_KEYS.TICKETS])
      queryClient.invalidateQueries([QUERY_KEYS.TICKET])
      queryClient.invalidateQueries([QUERY_KEYS.TO_AUTHORIZE])
    }

    socket.on('ticket-update', handleTicketUpdate)
    return () => {
      socket.off('ticket-update', handleTicketUpdate)
    }
  }, [socket, queryClient])

  const { equipmentOptions } = useEquipment()
  const {
    setTicketToSearch,
    ticketToSearch,
    menuDisplayed,
    handleDisplayFilters,
    filtersDisplayed,
  } = useGeneral()

  const { allCarriersOptions, isLoadingAllCarriers } = useCarriers()

  const { materialsOptions } = useMaterials()
  const [expandedRows, setExpandedRows] = useState<readonly Key[]>([])
  const [pendingStatusName, setPendingStatusName] = useState<string[]>(filters?.statusName ?? [])
  const { approveTicket } = useApproveTicket()
  const { createTicket, isLoading: isCreateTicketLoading } = useCreateTicket()
  const { suppliersOptions } = useSuppliers()

  const preparedData = useMemo(
    () => prepareDataToExpandableTable(tickets?.tickets) ?? [],
    [tickets],
  )
  const handleSetRowData: (expandedKeys?: readonly Key[]) => void = expandedKeys => {
    setExpandedRows(expandedKeys ?? [])
  }

  useEffect(() => {
    handleSetRowData()
  }, [filters])

  useEffect(() => {
    setPendingStatusName(filters?.statusName ?? [])
  }, [filters?.statusName])

  useEffect(() => {
    if (ticketToSearch && tickets?.total === 1) {
      const [ticket] = tickets?.tickets
      if (ticket) {
        setFilters((prevState: Filters) => ({
          ...prevState,
          dateFrom: dayjs(tickets?.tickets[0].withdrawalDate).startOf('d').toDate(),
          dateTo: dayjs(tickets?.tickets[0].withdrawalDate).endOf('d').toDate(),
        }))
      }
      handleSetRowData([tickets.tickets[0].withdrawalDay!])
    }
  }, [tickets])

  const orderByOptions = [
    {
      label: `${t('suppliers_router.filter_options.order_by.ascending')}`,
      value: 'ASC',
    },
    {
      label: `${t('suppliers_router.filter_options.order_by.descending')}`,
      value: 'DESC',
    },
  ]

  const filterOptions = [
    {
      label: `${t('admin_router.filter_options.status.confirmed')}`,
      value: StatusTicketEnum.CONFIRMED,
    },
    {
      label: `${t('admin_router.filter_options.status.supplier_invoiced')}`,
      value: StatusTicketEnum.TRANSIT,
    },
    {
      label: `${t('admin_router.filter_options.status.delivered')}`,
      value: StatusTicketEnum.DELIVERED,
    },
    {
      label: `${t('globals.generics.status.no_show')}`,
      value: StatusTicketEnum.NO_SHOW,
    },
  ]

  const handleStatusFilterChange = (value: any) => {
    setPendingStatusName([...(value as string[])])
  }

  const handleApplyStatusFilter = () => {
    setFilters((prevState: Filters) => ({
      ...prevState,
      page: 1,
      statusName: [...pendingStatusName],
      ticketId: undefined,
      invoiceNumber: undefined,
      taInvoiceNumber: undefined,
    }))
  }

  const handleMaterialFilterChange = (value: any) => {
    setFilters((prevState: Filters) => ({
      ...prevState,
      page: 1,
      materialId: [...(value as number[])],
      ticketId: undefined,
      invoiceNumber: undefined,
      taInvoiceNumber: undefined,
    }))
  }

  const handleOrderByFilterChange = (value: any) => {
    setFilters((prevState: Filters) => ({
      ...prevState,
      page: 1,
      orderBy: value as string,
      ticketId: undefined,
      invoiceNumber: undefined,
      taInvoiceNumber: undefined,
    }))
  }

  const handleEquipmentFilterChange = (value: any) => {
    setFilters((prevState: Filters) => ({
      ...prevState,
      page: 1,
      equipmentId: [...(value as number[])],
      ticketId: undefined,
      invoiceNumber: undefined,
      taInvoiceNumber: undefined,
    }))
  }

  const handleSupplierFilterChange = (value: any) => {
    setFilters((prevState: Filters) => ({
      ...prevState,
      page: 1,
      supplierId: [...(value as number[])],
      ticketId: undefined,
      invoiceNumber: undefined,
      taInvoiceNumber: undefined,
    }))
  }

  const handleCarrierFilterChange = (value: any) => {
    setFilters((prevState: Filters) => ({
      ...prevState,
      page: 1,
      carrierId: [...(value as number[])],
      ticketId: undefined,
      invoiceNumber: undefined,
      taInvoiceNumber: undefined,
    }))
  }

  const handleCleanFilters = () => {
    setTicketToSearch(undefined)
    setPendingStatusName([])
    handleSetRowData()
    resetFilters()
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFilterOptions = (input: string, option: any) => {
    if (option.label) return option.label.toString().toLowerCase().includes(input.toLowerCase())
    return true
  }

  const submitHandler = (
    id: number | undefined,
    form: CommonTicketForm,
    resetForm: () => void,
    dates: Date[] | undefined,
    toApproved?: boolean,
  ) => {
    if (id) {
      changeTicket(
        {
          id,
          payload: {
            ...form,
            ticketDate: dates![0],
          },
        },
        {
          onSuccess: () => {
            notification.success({
              message: `${t(
                'admin_router.admin_dashboard.use_approve_tickets.edit_success_evouchers',
              )} ${id}`,
            })
            if (toApproved) {
              approveTicket({ id, note: form.note })
            }
            resetForm()
          },
        },
      )
    } else {
      createTicket(
        {
          ...form,
          ticketsDate: dates,
        },
        {
          onSuccess: () => {
            resetForm()
          },
        },
      )
    }
  }
  const handleCloseTicketModal = () => {
    setIsTicketModalOpen(false)
  }
  const handleCleanSearchInput = () => {
    setTicketToSearch(undefined)
  }

  const ticketNotification = useContext(TicketNotifContext)
  const actionButtons = (ticket: Ticket) => {
    const { id } = ticket
    return (
      <ActionsCellContainer>
        <EyeOutlined
          onClick={() => {
            ticketNotification.setState(ticket.id)
            setTicketToEdit(id)
            setIsTicketModalOpen(true)
          }}
        />
      </ActionsCellContainer>
    )
  }

  const deliveryNoteTemplate = (_value: any, ticket: Ticket) => <DeliveryNote ticket={ticket} />

  const ticketDateTemplate = (_value: any, ticket: Ticket) => (
    <Tag className="tag-hidden">{dayjs(ticket.withdrawalDate).format('YYYY-MM-DD hh:mm')}</Tag>
  )

  const handleCloseMapsModal = () => {
    setIsMapsModalOpen(false)
  }

  const handleOpenMapsModal = (ticket: Ticket, title: string) => {
    setIsMapsModalOpen(true)
    if (title === 'origen') {
      setLocationLatitude(ticket.origin.location.coordinates[0])
      setLocationLongitude(ticket.origin.location.coordinates[1])
      setLocationStreet(ticket.origin.street)
      setLocationNumber(ticket.origin.number)
      setLocationState(ticket.origin.state)
      setLocationCity(ticket.origin.city)
      setTag(ticket.origin.tag)
    }
    if (title === 'destino') {
      setLocationLatitude(ticket.destination.location.coordinates[0])
      setLocationLongitude(ticket.destination.location.coordinates[1])
      setLocationStreet(ticket.destination.street)
      setLocationNumber(ticket.destination.number)
      setLocationState(ticket.destination.state)
      setLocationCity(ticket.destination.city)
      setTag(ticket.destination.tag)
    }
  }

  const locationBody = (ticket: Ticket, title: string) => (
    <Button
      type="link"
      className="location-button"
      onClick={() => {
        handleOpenMapsModal(ticket, title)
      }}
    >
      <span className="location-text">
        {title === 'origen' ? ticket.origin.tag : ticket.destination.tag}
      </span>
    </Button>
  )

  return (
    <>
      <ContentContainer>
        <StyledRow className="sticky-options border-botom">
          <div
            className={`filters-container${
              filtersDisplayed && !menuDisplayed ? ' filters-displayed' : ''
            }`}
          >
            <Button onClick={handleDisplayFilters} id="displayFiltersButton">
              <FilterOutlined />
            </Button>
            <div className="filters-list">
              <StyledButton className="inline option" onClick={handleCleanFilters}>
                {t('globals.generics.clean_filters')}
              </StyledButton>
              <div className="status-filter-apply-wrap">
                <StyledSelect
                  mode="multiple"
                  allowClear
                  placeholder={t('admin_router.filter_options.status.title')}
                  options={filterOptions}
                  onChange={handleStatusFilterChange}
                  filterOption={handleFilterOptions}
                  value={pendingStatusName}
                  onDropdownVisibleChange={handleCleanSearchInput}
                  maxTagCount="responsive"
                  className="status-filter-apply-select"
                />
                <div
                  className="status-filter-apply-trigger"
                  role="button"
                  tabIndex={0}
                  onClick={handleApplyStatusFilter}
                  onKeyDown={e => e.key === 'Enter' && handleApplyStatusFilter()}
                  aria-label={t('globals.generics.apply.title')}
                >
                  <SearchOutlined />
                </div>
              </div>
              <StyledSelect
                maxTagCount="responsive"
                mode="multiple"
                allowClear
                filterOption={handleFilterOptions}
                placeholder={t('suppliers_router.filter_options.carrier.name')}
                options={allCarriersOptions}
                loading={isLoadingAllCarriers}
                onChange={handleCarrierFilterChange}
                value={filters?.carrierId}
                onDropdownVisibleChange={handleCleanSearchInput}
              />
              <StyledSelect
                maxTagCount="responsive"
                mode="multiple"
                allowClear
                placeholder={t('suppliers_router.filter_options.material.title')}
                options={materialsOptions}
                filterOption={handleFilterOptions}
                onChange={handleMaterialFilterChange}
                value={filters?.materialId}
                onDropdownVisibleChange={handleCleanSearchInput}
              />
              <StyledSelect
                maxTagCount="responsive"
                mode="multiple"
                allowClear
                placeholder={t('suppliers_router.filter_options.equipment.title')}
                options={equipmentOptions}
                filterOption={handleFilterOptions}
                onChange={handleEquipmentFilterChange}
                value={filters?.equipmentId}
                onDropdownVisibleChange={handleCleanSearchInput}
              />
              <StyledSelect
                maxTagCount="responsive"
                mode="multiple"
                allowClear
                placeholder={t('admin_router.filter_options.supplier')}
                options={suppliersOptions}
                filterOption={handleFilterOptions}
                onChange={handleSupplierFilterChange}
                value={filters?.supplierId}
                onDropdownVisibleChange={handleCleanSearchInput}
              />
              <StyledSelect
                maxTagCount="responsive"
                allowClear
                placeholder={t('suppliers_router.filter_options.order_by.title')}
                options={orderByOptions}
                filterOption={handleFilterOptions}
                onChange={handleOrderByFilterChange}
                value={filters?.orderBy}
                onDropdownVisibleChange={handleCleanSearchInput}
              />
            </div>
          </div>

          <CollapseExpand handleSetRowData={handleSetRowData} preparedData={preparedData} />
        </StyledRow>
        <StyledRow className="ticket-handler">
          <div style={{ flex: 1 }} />
          <DateFromToFilter time={TIME.EXPIRED} />
          <div style={{ flex: 1 }} />
        </StyledRow>
        <Spin spinning={ticketsIsLoading} style={{ marginTop: '25px' }}>
          <StyledRow className="data-table-container admin">
            <ExpandableTable
              expandedRows={expandedRows}
              handleSetRowData={handleSetRowData}
              data={preparedData}
              columns={[
                {
                  title: 'ID',
                  dataIndex: 'id',
                  key: 'id',
                  align: 'center',
                  className: 'column-id text-styles-row',
                  render: (text: string) => <div>{text}</div>,
                },
                {
                  title: 'Remito',
                  dataIndex: 'deliveryNote',
                  key: 'deliveryNote',
                  className: 'column-width text-styles-row',
                  render: deliveryNoteTemplate,
                },
                {
                  title: 'Origen',
                  key: 'origen',
                  className: 'column-width text-styles-row',
                  render: (_value, ticket) => locationBody(ticket, 'origen'),
                },
                {
                  title: 'Destino',
                  key: 'destino',
                  className: 'column-width text-styles-row',
                  render: (_value, ticket) => locationBody(ticket, 'destino'),
                },
                {
                  title: 'Material',
                  dataIndex: ['material', 'nameShort'],
                  key: 'material.nameShort',
                  className: 'column-width text-styles-row',
                  render: value => <Tag className="tag-hidden">{value}</Tag>,
                },
                {
                  title: 'Equipo',
                  dataIndex: 'equipment',
                  key: 'equipment',
                  className: 'column-width',
                  render: equipment => (
                    <Tag
                      style={{ color: theme.textColor, width: '90%' }}
                      className="text-styles-row"
                      color={'#' + equipment.color}
                    >
                      {equipment.nameShort}
                    </Tag>
                  ),
                },
                {
                  title: 'Fecha',
                  dataIndex: 'withdrawalDate',
                  key: 'withdrawalDate',
                  sorter: true,
                  className: 'column-width text-styles-row',
                  render: ticketDateTemplate,
                },
                {
                  title: 'Transportista',
                  dataIndex: ['carrier', 'businessName'],
                  key: 'carrier.businessName',
                  className: 'column-width text-styles-row',
                  render: value => value,
                },
                {
                  title: 'Estado',
                  dataIndex: ['status', 'name'],
                  key: 'status.name',
                  className: 'column-width text-styles-row',
                  render: (_value, { status }) =>
                    t(`globals.generics.status.${status.name.toLowerCase()}`),
                },
                {
                  title: 'Chofer',
                  key: 'chofer',
                  align: 'center',
                  className: 'column-width text-styles-row',
                  render: (_text, ticket) => <DriverBarlight ticket={ticket} />,
                },
                {
                  title: 'Acciones',
                  key: 'acciones',
                  align: 'center',
                  className: 'column-width',
                  render: actionButtons,
                },
              ]}
            />
          </StyledRow>
        </Spin>
      </ContentContainer>
      <UpdateTicketModalAdmin
        title={t('suppliers_router.new_ticket_modal.title')}
        onClose={callback => {
          handleCloseTicketModal()
          if (callback) {
            callback()
          }
        }}
        onOk={submitHandler}
        isLoading={isCreateTicketLoading}
      />
      <MapsModal
        open={isMapsModalOpen}
        longitude={locationLongitude}
        latitude={locationLatitude}
        street={locationStreet}
        number={locationNumber}
        province={locationState}
        city={locationCity}
        close={handleCloseMapsModal}
        tag={tag}
      />
    </>
  )
}

export default AdminEVouchers
